import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { FinancialsTable } from "../financials/FinancialsTable";
import { Panel, money2, count as fmt } from "../lux";
import { CaretRight, Funnel, Timer, XCircle } from "@phosphor-icons/react";

const STAGE_LABEL = { new: "New", contacted: "Contacted", quoted: "Quoted", won: "Won" };

function FunnelStrip({ funnel, lostTotal }) {
  return (
    <div className="flex flex-wrap items-stretch gap-2" data-testid="lead-funnel">
      {(funnel || []).map((s, i) => (
        <div key={s.stage} className="flex items-stretch gap-2">
          {i > 0 && (
            <div className="flex flex-col items-center justify-center px-1">
              <CaretRight size={14} className="text-muted-foreground" />
              {s.drop_off_pct !== null && s.drop_off_pct !== undefined && (
                <span className="text-[10px] text-red-400 tnum">−{s.drop_off_pct}%</span>
              )}
            </div>
          )}
          <div className="border border-border bg-card px-5 py-4 min-w-[130px]" data-testid={`funnel-${s.stage}`}>
            <div className="label-mono">{STAGE_LABEL[s.stage] || s.stage}</div>
            <div className="font-heading text-3xl tnum mt-1.5">{fmt(s.count)}</div>
          </div>
        </div>
      ))}
      <div className="border border-red-500/30 bg-red-500/[0.05] px-5 py-4 min-w-[130px] ml-2" data-testid="funnel-lost">
        <div className="label-mono text-red-400">Lost</div>
        <div className="font-heading text-3xl tnum mt-1.5 text-red-400">{fmt(lostTotal)}</div>
      </div>
    </div>
  );
}

export function LeadsTab({ range, moneyOk }) {
  const { data, isLoading } = useQuery({
    queryKey: ["sales-leads", range.from, range.to],
    queryFn: async () => (await api.get("/sales-performance/leads", {
      params: { from: range.from || undefined, to: range.to || undefined },
    })).data,
  });

  return (
    <div className="space-y-7">
      <FunnelStrip funnel={data?.funnel} lostTotal={data?.lost_total} />

      <Panel label="Where the money comes from" title="By source" icon={Funnel} testid="by-source-panel" flush>
        <FinancialsTable
          testid="by-source-table"
          exportName="leads-by-source"
          loading={isLoading}
          rows={data?.by_source || []}
          initialSort={{ key: moneyOk ? "revenue" : "leads", dir: "desc" }}
          empty="No leads in this period."
          columns={[
            { key: "source", label: "Source" },
            { key: "leads", label: "Leads", align: "right" },
            { key: "contacted_pct", label: "Contacted %", align: "right", fmt: (v) => `${v}%` },
            { key: "won", label: "Won", align: "right" },
            { key: "close_pct", label: "Close %", align: "right", fmt: (v) => `${v}%` },
            ...(moneyOk ? [
              { key: "revenue", label: "Revenue", align: "right", fmt: money2, tone: "gold" },
              { key: "revenue_per_lead", label: "Revenue / lead", align: "right", fmt: money2 },
            ] : []),
          ]}
        />
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        <Panel label="Why we lose" title="Lost reasons" icon={XCircle} testid="lost-reasons-panel" flush>
          <div className="divide-y divide-border/50">
            {(data?.lost_reasons || []).length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Nothing lost in this period.</div>}
            {(data?.lost_reasons || []).map((r) => (
              <div key={r.reason} className="px-5 py-3 flex items-center gap-4" data-testid={`lost-reason-${r.reason}`}>
                <span className="flex-1 text-sm">{r.reason}</span>
                <span className="tnum text-sm">{fmt(r.count)}</span>
                <span className="tnum text-xs text-muted-foreground w-12 text-right">{r.pct}%</span>
              </div>
            ))}
          </div>
        </Panel>

        <Panel label="Speed wins" title="Response time vs win rate" icon={Timer} testid="response-buckets-panel" flush>
          <div className="divide-y divide-border/50">
            {(data?.response_buckets || []).map((b) => (
              <div key={b.bucket} className="px-5 py-3" data-testid={`response-bucket-${b.bucket}`}>
                <div className="flex items-center gap-4">
                  <span className="flex-1 text-sm">{b.bucket}</span>
                  <span className="tnum text-sm text-muted-foreground">{fmt(b.leads)} leads</span>
                  <span className="tnum text-sm text-primary w-16 text-right">{b.win_rate}% won</span>
                </div>
                <div className="h-1.5 mt-2 w-full bg-white/[0.06] rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(100, b.win_rate)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

export function VehicleDemandTab({ range }) {
  const { data, isLoading } = useQuery({
    queryKey: ["sales-demand", range.from, range.to],
    queryFn: async () => (await api.get("/sales-performance/vehicle-demand", {
      params: { from: range.from || undefined, to: range.to || undefined },
    })).data,
  });
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        What clients ask for on the website — and what we turn away. Sourced from the car requested on each lead.
      </p>
      <FinancialsTable
        testid="vehicle-demand-table"
        exportName="vehicle-demand"
        loading={isLoading}
        rows={data?.rows || []}
        initialSort={{ key: "leads", dir: "desc" }}
        empty="No leads with a requested car in this period."
        columns={[
          { key: "vehicle", label: "Vehicle requested" },
          { key: "leads", label: "Leads", align: "right" },
          { key: "booked", label: "Booked", align: "right" },
          { key: "conversion_pct", label: "Conversion %", align: "right", fmt: (v) => `${v}%` },
          { key: "lost_no_vehicle", label: 'Lost — "no vehicle available"', align: "right" },
        ]}
      />
    </div>
  );
}
